/**
 * megaScanApply — what a scanned page teaches the Mega surfaces (2026-09-03
 * owner ask: "connect the scanner to the dex entries").
 *
 * Two pages carry Mega facts:
 *
 *   - the caught-Pokémon page: the level circles per form (level + progress)
 *     and the rest badge ("4 DAYS"). The record itself takes the level in
 *     myPokemonStore.mergeInto; THIS applies the species-wide side: the
 *     form's level in megaLevelStore (raise only), its progress and rest,
 *     and the Mega dex cell, since a filled circle proves the trainer has
 *     Mega Evolved that form at least once.
 *   - the Mega Level page ("Mewtwo's Mega Level"): the banner level, "N more
 *     times" (an exact count toward the next level), the rest period and
 *     the form's energy. No record to attach to (the page does not say
 *     which Mewtwo), so it feeds the species-wide store and the wallet.
 *
 * Both scan paths (live and screenshot upload) call this after the OCR.
 */
import { getDexEntryState, setManual } from "./dexStateStore";
import { megaFormKeyForLabel, megaNextLevel, parseMegaFormKey, type MegaLevel } from "./megaLevels";
import { noteMegaScan } from "./megaLevelStore";
import type { MyPokemonFrame } from "./myPokemonOcr";
import { speciesIdFromDisplayName } from "./myPokemonOcr";
import { recordTrainerResources } from "./trainerResources";

export interface MegaApplyResult {
  /** Forms whose store entry was written. */
  forms: string[];
  /** Mega dex cells newly marked present. */
  dexCells: number;
}

function ensureMegaCell(formKey: string): boolean {
  const parsed = parseMegaFormKey(formKey);
  if (!parsed) return false;
  if (getDexEntryState("mega", parsed.dexId, parsed.formId)?.state === "present") return false;
  return setManual("mega", parsed.dexId, "present", parsed.formId);
}

/** The caught-Pokémon page's Mega facts, or the Mega Level page's. */
export function applyMegaFrame(
  frame: Pick<
    MyPokemonFrame,
    "speciesId" | "megaReads" | "megaRestMs" | "isMegaLevelPage" | "megaLevelPage"
  >,
  observedAt = Date.now(),
): MegaApplyResult {
  const out: MegaApplyResult = { forms: [], dexCells: 0 };

  if (frame.isMegaLevelPage && frame.megaLevelPage) {
    const page = frame.megaLevelPage;
    const speciesId = speciesIdFromDisplayName(page.speciesName);
    if (speciesId == null) return out;
    const formKey = megaFormKeyForLabel(speciesId, page.formLabel);
    if (!formKey) return out;
    if (page.energy != null) {
      recordTrainerResources({
        megaEnergies: [{ speciesId, formLabel: page.formLabel, energy: page.energy }],
        observedAt,
      });
    }
    if (page.level != null) {
      const next = megaNextLevel(page.level);
      const evolutions =
        page.remainingToNext != null && next?.evolutionsToReach != null
          ? Math.max(0, next.evolutionsToReach - page.remainingToNext)
          : null;
      noteMegaScan(
        formKey,
        {
          level: page.level,
          evolutions,
          restEndsAt: page.restMs != null ? observedAt + page.restMs : null,
        },
        observedAt,
      );
      out.forms.push(formKey);
      if (ensureMegaCell(formKey)) out.dexCells += 1;
    }
    return out;
  }

  if (frame.speciesId == null) return out;
  const restEndsAt = frame.megaRestMs != null ? observedAt + frame.megaRestMs : null;
  for (const read of frame.megaReads ?? []) {
    if (read.level < 1) continue;
    const formKey = megaFormKeyForLabel(frame.speciesId, read.formLabel);
    if (!formKey) continue;
    noteMegaScan(
      formKey,
      {
        level: read.level as MegaLevel,
        progressToNext: read.progressToNext > 0 ? read.progressToNext : null,
        // The rest period belongs to the Pokémon; it lands on the forms it
        // has actually evolved.
        restEndsAt,
      },
      observedAt,
    );
    out.forms.push(formKey);
    if (ensureMegaCell(formKey)) out.dexCells += 1;
  }
  return out;
}
